import type { FC } from 'react';
import { useMemo } from 'react';
import { Checkbox } from '@chakra-ui/react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { ListReveal } from '../../../../../components/list-reveal/ListReveal';
import { Text } from '../../../../../ui-components/typography';
import type { RangeFilter, UseSearchResult } from '../../../hooks/use-search';

const messages = defineMessages({
    tenToTwenty: {
        id: 'search.filters.savings.10-20',
        defaultMessage: '10% - 20%',
    },
    twentyToThirty: {
        id: 'search.filters.savings.20-30',
        defaultMessage: '20% - 30%',
    },
    thirtyToForty: {
        id: 'search.filters.savings.30-40',
        defaultMessage: '30% - 40%',
    },
    fortyToFifty: {
        id: 'search.filters.savings.40-50',
        defaultMessage: '40% - 50%',
    },
    fiftyUp: {
        id: 'search.filters.savings.50-up',
        defaultMessage: '50% & Up',
    },
});

const savingsCountMap: Record<string, MessageDescriptor> = {
    '10': messages.tenToTwenty,
    '20': messages.twentyToThirty,
    '30': messages.thirtyToForty,
    '40': messages.fortyToFifty,
    '50': messages.fiftyUp,
};

export const SavingsFilterSection: FC<{ search: UseSearchResult }> = ({ search }) => {
    const intl = useIntl();
    const { filters, onChangeFilter, isFilterSelected } = search;

    const savingsRanges = useMemo(
        () =>
            ((filters?.byRange?.saving as RangeFilter[]) || []).filter(
                (range: RangeFilter) => typeof range.start === 'number' && range.start >= 10,
            ),
        [filters?.byRange?.saving],
    );

    return (
        <ListReveal
            data={savingsRanges}
            keyExtractor={(range) => range.start.toString()}
            listContainerClassName="flex flex-col gap-4 ml-[2px]"
            renderItem={({ item: range }) => {
                const value = `${range.start}-${range.end}`;
                return (
                    <Checkbox
                        variant="new-style"
                        key={value}
                        isChecked={isFilterSelected('saving', value)}
                        onChange={() => onChangeFilter('saving', value)}
                        data-testid={`savings-checkbox-${value}`}
                        isDisabled={range.count === 0}
                    >
                        <Text>{`${intl.formatMessage(savingsCountMap[range.start.toString()])} (${range.count})`}</Text>
                    </Checkbox>
                );
            }}
        />
    );
};
